/**
 * Centrale bron van alle vaktermen die op de site in tooltips uitgelegd
 * worden. Zowel de tooltips (in `sections.tsx`) als de woordenlijst-pagina
 * (`/woordenlijst`) lezen hun teksten uit dezelfde i18n-sleutels, zodat er
 * maar één vertaalbron per term bestaat.
 */

export type GlossaryGroupId = "jargon" | "templates";

export type GlossaryEntry = {
  /** URL-anker op de woordenlijst-pagina, bv. `#smart-routing`. */
  slug: string;
  /** Groep waarin de term getoond wordt. */
  group: GlossaryGroupId;
  /** i18n-sleutel (namespace `home`) voor de titel van de term. */
  titleKey: string;
  /** i18n-sleutel (namespace `home`) voor de uitleg. */
  introKey: string;
  /** i18n-sleutel (namespace `home`) voor de lijst met voorbeelden. */
  typesKey: string;
};

/** De 20 jargon-termen uit `packages.glossary.*`. */
const GLOSSARY_TERM_KEYS = [
  "enterprise_api",
  "smart_routing",
  "mcp",
  "geo_targeting",
  "zero_downtime",
  "rate_limiting",
  "ai_readiness",
  "edge_caching",
  "waf",
  "bot_management",
  "image_optimization",
  "http3",
  "data_residency",
  "core_web_vitals",
  "deliverability",
  "ip_transfer",
  "sso",
  "owasp",
  "ddos",
  "geo_seo",
] as const;

const jargonEntries: GlossaryEntry[] = GLOSSARY_TERM_KEYS.map((key) => ({
  slug: key.replace(/_/g, "-"),
  group: "jargon",
  titleKey: `packages.glossary.${key}.title`,
  introKey: `packages.glossary.${key}.intro`,
  typesKey: `packages.glossary.${key}.types`,
}));

/** De 4 uitleg-tooltips rond templates, integraties en CMS. */
const templateEntries: GlossaryEntry[] = [
  {
    slug: "paginatypes-templates",
    group: "templates",
    titleKey: "packages.hint_title",
    introKey: "packages.hints.pages",
    typesKey: "packages.template_types",
  },
  {
    slug: "paginatypes-premium",
    group: "templates",
    titleKey: "packages.hint_title",
    introKey: "packages.hints.pages_premium",
    typesKey: "packages.template_types_premium",
  },
  {
    slug: "zware-integraties",
    group: "templates",
    titleKey: "packages.hint_title",
    introKey: "packages.hints.integrations",
    typesKey: "packages.integration_types",
  },
  {
    slug: "headless-cms",
    group: "templates",
    titleKey: "packages.cms_hint_title",
    introKey: "packages.hints.headless_cms",
    typesKey: "packages.headless_cms_types",
  },
];

export const GLOSSARY_ENTRIES: GlossaryEntry[] = [...jargonEntries, ...templateEntries];

/** Alle term-slugs (taalneutraal) — gebruikt voor routes, sitemap en validatie. */
export const GLOSSARY_SLUGS: string[] = GLOSSARY_ENTRIES.map((e) => e.slug);

/** Zoek een term op zijn URL-slug (`/woordenlijst/<slug>`). */
export function glossaryEntryBySlug(slug: string): GlossaryEntry | undefined {
  return GLOSSARY_ENTRIES.find((e) => e.slug === slug);
}

/**
 * Vind het anker van een term op basis van de i18n-sleutel die een tooltip
 * gebruikt (bv. `packages.glossary.sso.types` → `sso`).
 */
export function glossarySlugForTypesKey(typesKey?: string): string | undefined {
  if (!typesKey) return undefined;
  return GLOSSARY_ENTRIES.find((e) => e.typesKey === typesKey)?.slug;
}
